// request_signer.ts — firma de peticiones del plano de datos (Fase 2C).
//
// El plano de datos de IKOGNIT (archive, universo privado) NO se autentica con el
// JWT de cuenta, sino firmando cada petición con la clave Ed25519 del
// pseudónimo activo. El servidor (requirePseudonym) verifica la firma y deriva
// P = SHA-256(PK) sin aprender jamás qué cuenta hay detrás.
//
// Mensaje canónico firmado (UTF-8, líneas unidas por '\n') — debe coincidir
// EXACTAMENTE con backend/src/http/pseudonym.ts:
//   METHOD
//   path                   (path + querystring, tal cual se envía)
//   base64(SHA-256(body))   (body crudo; vacío => hash de 0 bytes)
//   timestamp_ms
//   nonce

import sodium from 'libsodium-wrappers-sumo';
import { sha256 } from '@noble/hashes/sha2.js';
import * as crypto from './crypto';

export interface SigningKeyPair {
  publicKey: Uint8Array;   // Ed25519, 32 bytes
  privateKey: Uint8Array;  // Ed25519, 64 bytes (formato libsodium)
}

// Pseudónimo activo. Lo fija Auth (identidad general) y freq_session (un IKO).
let signingKeyPair: SigningKeyPair | null = null;

export function setSigningKeyPair(kp: SigningKeyPair | null): void {
  signingKeyPair = kp;
}

export function hasSigningKeyPair(): boolean {
  return signingKeyPair !== null;
}

/**
 * Deriva la identidad firmadora Ed25519 de la cuenta GENERAL a partir de su
 * clave privada X25519. La X25519 es estable de por vida (el cambio de
 * contraseña la re-cifra, no la regenera; el recovery BIP39 la recupera
 * idéntica), así que el pseudónimo general es estable y multidevice.
 * El servidor solo conoce la X25519 *pública* → no puede derivar este
 * pseudónimo → el archivo de la identidad general también queda desvinculado.
 */
export function deriveGeneralSigningKeyPair(x25519PrivateKey: Uint8Array): SigningKeyPair {
  const seed = crypto.hkdf(
    x25519PrivateKey,
    new Uint8Array(0),
    new TextEncoder().encode('ikognit-general-signing-v1'),
    32,
  );
  const kp = sodium.crypto_sign_seed_keypair(seed);
  return { publicKey: kp.publicKey, privateKey: kp.privateKey };
}

/**
 * Construye las cabeceras X-IKOGNIT-* firmando la petición con el pseudónimo
 * activo. Devuelve null si no hay keypair de firma (el caller debe entonces
 * caer al JWT como antes — sólo ocurre antes del primer login).
 *
 * `bodyBytes` deben ser EXACTAMENTE los bytes que se envían como body. Para
 * peticiones sin body, pasar un Uint8Array vacío. Para subidas binarias en
 * streaming (private body) pasar vacío: el servidor no puede hashear el
 * stream, así que la firma cubre método+ruta+ts+nonce (la ruta lleva el id).
 */
export function signedHeaders(
  method: string,
  path: string,
  bodyBytes: Uint8Array,
): Record<string, string> | null {
  if (!signingKeyPair) return null;
  return signedHeadersWith(signingKeyPair, method, path, bodyBytes);
}

/**
 * Igual que `signedHeaders` pero firmando con un keypair EXPLÍCITO en vez del
 * pseudónimo activo global. Necesario para operar con una identidad ad-hoc sin
 * cambiar el contexto activo — p. ej. una CÁPSULA de almacenamiento conectada a
 * un IKO: estás dentro del IKO (pseudónimo activo) y a la vez lees/escribes los
 * ficheros de la cápsula (su propia clave), sin salir del IKO.
 */
export function signedHeadersWith(
  kp: SigningKeyPair,
  method: string,
  path: string,
  bodyBytes: Uint8Array,
): Record<string, string> {
  const ts = String(Date.now());
  const nonce = crypto.b64encode(sodium.randombytes_buf(16));
  const bodyHash = crypto.b64encode(sha256(bodyBytes));
  const canonical = new TextEncoder().encode(
    [method.toUpperCase(), path, bodyHash, ts, nonce].join('\n'),
  );
  const sig = crypto.signDetached(canonical, kp.privateKey);
  return {
    'X-IKOGNIT-PK': crypto.b64encode(kp.publicKey),
    'X-IKOGNIT-TS': ts,
    'X-IKOGNIT-Nonce': nonce,
    'X-IKOGNIT-Sig': crypto.b64encode(sig),
  };
}

/**
 * Query string para un connect firmado del WebSocket. El WS del navegador no
 * admite cabeceras propias, así que las 4 piezas (pk/ts/nonce/sig) viajan en
 * el query string, URL-encoded. La firma cubre el mensaje canónico de un
 * `GET /ws` sin body — exactamente lo que verifica el backend en
 * ws/server.ts. Devuelve null si no hay pseudónimo activo.
 */
export function signedWsConnectQuery(): string | null {
  const h = signedHeaders('GET', '/ws', new Uint8Array(0));
  if (!h) return null;
  const e = encodeURIComponent;
  return `pk=${e(h['X-IKOGNIT-PK'])}&ts=${e(h['X-IKOGNIT-TS'])}` +
    `&nonce=${e(h['X-IKOGNIT-Nonce'])}&sig=${e(h['X-IKOGNIT-Sig'])}`;
}

/**
 * Identificador de pseudónimo tal como lo deriva el backend (requirePseudonym):
 * P = base64url(SHA-256(clave pública Ed25519)). Sirve para consultar el
 * registro de miembros del IKO por el pseudónimo de un peer.
 */
export function pseudonymId(ed25519PublicKey: Uint8Array): string {
  return sodium.to_base64(sha256(ed25519PublicKey), sodium.base64_variants.URLSAFE_NO_PADDING);
}
